"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Container,
  Chip,
  Divider,
  Paper,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  ShoppingCart,
  LocalOffer,
  Security,
  LocalShipping,
  Payment,
  CheckCircle,
  Info,
  Celebration,
} from "@mui/icons-material";

const Checkout = () => {
  const [products, setProducts] = useState([]);
  const [userDiscount, setUserDiscount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [gstTotal, setGstTotal] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [userId, setUserId] = useState(1);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const userData = JSON.parse(localStorage.getItem("userData")) || {};

    const discount = userData.discount || 0;
    setUserDiscount(discount);

    const fetchProducts = async () => {
      setLoading(true);
      const productPromises = cart.map(async (item) => {
        try {
          const response = await axios.get(
            `https://namami-infotech.com/EvaraBackend/src/sku/get_sku.php?medicine_id=${item.id}`
          );
          return {
            ...response.data.medicine,
            quantity: item.quantity,
          };
        } catch (error) {
          console.error("Error fetching product details:", error);
          return null;
        }
      });

      const productData = (await Promise.all(productPromises)).filter(
        (product) => product !== null
      );
      setProducts(productData);
      setLoading(false);
    };

    fetchProducts();
  }, []);

  const calculateTotals = () => {
    let total = 0;
    let discount = 0;
    let gstAmount = 0;

    products.forEach((product) => {
      const basePrice = Number.parseFloat(product.ptr);
      const quantity = product.quantity;

      let effectiveQuantity = quantity;
      let offerQuantity = 0;

      if (product.offer && product.offer.startsWith("Buy")) {
        const [buy, get] = product.offer.match(/\d+/g).map(Number);
        const sets = Math.floor(quantity / buy);
        offerQuantity = sets * get;
      }

      effectiveQuantity += offerQuantity;

      const priceWithDiscount =
        product.category === "Pharma"
          ? basePrice * (1 - userDiscount / 100)
          : basePrice;

      const productTotal = priceWithDiscount * quantity;
      total += productTotal;

      if (product.category === "Pharma") {
        discount += basePrice * quantity * (userDiscount / 100);
      }

      const gst = product.gst
        ? (productTotal - discount) * (Number.parseFloat(product.gst) / 100)
        : 0;
      gstAmount += gst;
    });

    setTotalPrice(total);
    setTotalDiscount(discount);
    setGstTotal(gstAmount);
    setGrandTotal(total - discount + gstAmount);
  };

  useEffect(() => {
    if (products.length > 0) {
      calculateTotals();
    }
  }, [products]);

  const handlePlaceOrder = async () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const orderProducts = cart.map((item) => {
      const product = products.find((p) => p.id === item.id);
      let offerQuantity = 0;
      let sets = 0;

      if (product && product.offer && product.offer.startsWith("Buy")) {
        const [buy, get] = product.offer.match(/\d+/g).map(Number);
        sets = Math.floor(item.quantity / buy);
        offerQuantity = sets * get;
      }

      const netQuantity = item.quantity + offerQuantity;

      return {
        product_id: item.id,
        net_quantity: netQuantity,
        buy_quantity: item.quantity,
        offer_quantity: offerQuantity,
        sets: sets,
      };
    });

    const orderPayload = {
      user_id: userId,
      products: orderProducts,
      total_amount: grandTotal,
    };

    try {
      const response = await axios.post(
        "https://namami-infotech.com/EvaraBackend/src/order/order_product.php",
        orderPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        setOrderSuccess("Order placed successfully!");
      } else {
        setOrderSuccess("Failed to place order. Please try again.");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      setOrderSuccess("Error placing order. Please try again.");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {[1, 2, 3].map((item) => (
                <Card key={item} sx={{ mb: 2, borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={100}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={30} />
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="text" width="30%" height={20} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, position: "sticky", top: 20 }}>
                <CardContent>
                  <Skeleton variant="text" width="50%" height={30} />
                  <Skeleton variant="text" width="70%" height={20} />
                  <Skeleton variant="text" width="60%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            borderRadius: 4,
            p: 3,
            mb: 3,
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <ShoppingCart />
            </Avatar>
            <Typography
              variant="h4"
              sx={{ color: "white", fontWeight: "bold" }}
            >
              Order Summary
            </Typography>
          </Box>

          {orderSuccess && (
            <Alert
              severity={orderSuccess.includes("success") ? "success" : "error"}
              sx={{ mb: 2, borderRadius: 2 }}
              icon={
                orderSuccess.includes("success") ? <CheckCircle /> : <Info />
              }
            >
              {orderSuccess}
            </Alert>
          )}

          {userDiscount && userDiscount !== 0 && userDiscount !== "" && (
            <Alert
              severity="success"
              sx={{ borderRadius: 2 }}
              icon={<Celebration />}
            >
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                🎉 Hurray! You unlock {userDiscount}% discount on Pharma
                Products
              </Typography>
            </Alert>
          )}
        </Paper>

        <Grid container spacing={3}>
          {/* Products Section */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {products.map((product, index) => {
                const sets =
                  product.offer && product.offer.startsWith("Buy")
                    ? Math.floor(
                        product.quantity /
                          Number.parseInt(product.offer.split(" ")[1])
                      )
                    : 0;

                const offerQuantity =
                  product.offer && product.offer.startsWith("Buy")
                    ? sets * Number.parseInt(product.offer.split(" ")[3])
                    : 0;
                const effectiveQuantity = product.quantity + offerQuantity;

                const showOfferAlert =
                  product.offer &&
                  product.quantity <
                    Number.parseInt(product.offer.split(" ")[1]);

                return (
                  <Card
                    key={index}
                    elevation={0}
                    sx={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 3,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <Box
                            component="img"
                            src={product.image_url}
                            alt={product.name}
                            sx={{
                              width: "100%",
                              maxWidth: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "2px solid #f0f0f0",
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", mb: 1 }}
                          >
                            {product.name}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={`₹${product.ptr}`}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                            <Chip
                              label={`Qty: ${product.quantity}`}
                              variant="outlined"
                              size="small"
                            />
                            {offerQuantity > 0 && (
                              <Chip
                                label={`+${offerQuantity} Free`}
                                color="success"
                                size="small"
                                icon={<LocalOffer />}
                              />
                            )}
                            <Chip
                              label={`GST: ${product.gst || 0}%`}
                              variant="outlined"
                              size="small"
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Total Quantity: <strong>{effectiveQuantity}</strong>
                          </Typography>

                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold" }}
                          >
                            Total Price: ₹
                            {(product.ptr * product.quantity).toFixed(2)}
                          </Typography>

                          {product.category === "Pharma" && (
                            <Typography
                              variant="body2"
                              color="success.main"
                              sx={{ fontWeight: "bold" }}
                            >
                              Discounted Price: ₹
                              {(
                                Number.parseFloat(
                                  product.ptr * product.quantity
                                ) *
                                (1 - userDiscount / 100)
                              ).toFixed(2)}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          {showOfferAlert && (
                            <Alert
                              severity="info"
                              sx={{ mb: 1, borderRadius: 2 }}
                            >
                              <Typography variant="caption">
                                {product.offer}
                              </Typography>
                            </Alert>
                          )}
                          {!showOfferAlert && product.offer && sets > 0 && (
                            <Alert
                              severity="success"
                              sx={{ mb: 1, borderRadius: 2 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: "bold" }}
                              >
                                🎉 You unlock {sets} free products!
                              </Typography>
                            </Alert>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Grid>

          {/* Order Summary Sidebar */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: 3,
                border: "1px solid rgba(255, 255, 255, 0.2)",
                position: "sticky",
                top: 20,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Payment color="primary" />
                  Payment Summary
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      ₹{totalPrice.toFixed(2)}
                    </Typography>
                  </Box>

                  {totalDiscount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="success.main">
                        Discount:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ fontWeight: "bold" }}
                      >
                        -₹{totalDiscount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">GST:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      ₹{gstTotal.toFixed(2)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Grand Total:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "primary.main" }}
                    >
                      ₹{grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {/* Trust Badges */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center", p: 1 }}>
                        <Security
                          color="primary"
                          sx={{ fontSize: 20, mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Secure
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center", p: 1 }}>
                        <LocalShipping
                          color="primary"
                          sx={{ fontSize: 20, mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Fast Delivery
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center", p: 1 }}>
                        <CheckCircle
                          color="primary"
                          sx={{ fontSize: 20, mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Verified
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handlePlaceOrder}
                  disabled
                  sx={{
                    background:
                      "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    borderRadius: 3,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                    },
                  }}
                >
                  Place Order
                </Button>

                <Typography
                  variant="caption"
                  display="block"
                  sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}
                >
                  By placing this order, you agree to our terms and conditions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Checkout;
